// src/pages/SubstituteDirectory.js - Directory of approved substitute teachers
import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp
} from 'firebase/firestore';

const SubstituteDirectory = ({ currentUser }) => {
  const [substitutes, setSubstitutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [ageGroupFilter, setAgeGroupFilter] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [selectedSubstitute, setSelectedSubstitute] = useState(null);
  
  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [editingSubstitute, setEditingSubstitute] = useState(null); // null = add new, object = editing
  const [saving, setSaving] = useState(false);
  const [substituteForm, setSubstituteForm] = useState({
    firstName: '',
    lastName: '',
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    emergencyContact: '',
    emergencyPhone: '',
    yearsExperience: '',
    previousExperience: '',
    certifications: [],
    ageGroupPreferences: [],
    subjectAreas: [],
    safeguardingTraining: false,
    cprTraining: false,
    backgroundCheckStatus: 'not_started',
    status: 'active',
    availability: {
      fullTime: false,
      partTime: false,
      emergencyOnly: false,
      longTerm: false
    },
    additionalInfo: ''
  });

  const ageGroupOptions = [
    'PreK (Ages 3-4)', 'Lower Elementary (K-2)', 'Upper Elementary (3-5)', 
    'Middle School (6-8)', 'High School (9-12)'
  ];

  const subjectOptions = [
    'Elementary (All Subjects)', 'English/Language Arts', 'Mathematics', 'Science', 
    'Social Studies', 'Foreign Language', 'Art', 'Music', 'Physical Education', 
    'Special Education', 'Library/Media', 'Technology'
  ];

  const certificationOptions = [
    'Teaching License/Certificate', 'Paraprofessional Certificate', 'Bachelor\'s Degree', 
    'Master\'s Degree', 'Subject-Specific Certification', 'ESL Certification', 
    'Special Education Certification', 'Early Childhood Education'
  ];

  useEffect(() => {
    if (currentUser?.tenantId) {
      loadSubstitutes();
    }
  }, [currentUser, statusFilter]);

  const loadSubstitutes = async () => {
    try {
      setLoading(true);
      
      let q;
      if (statusFilter === 'all') {
        q = query(
          collection(db, 'faculty_staff'),
          where('tenantId', '==', currentUser.tenantId),
          where('role', '==', 'Outside Sub'),
          orderBy('lastName', 'asc')
        );
      } else {
        q = query(
          collection(db, 'faculty_staff'),
          where('tenantId', '==', currentUser.tenantId),
          where('role', '==', 'Outside Sub'),
          where('status', '==', statusFilter),
          orderBy('lastName', 'asc')
        );
      }
      
      const snapshot = await getDocs(q);
      const substitutesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setSubstitutes(substitutesData);
      
    } catch (err) {
      console.error('Error loading substitutes:', err);
      setError('Failed to load substitute teachers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingSubstitute(null);
    setSubstituteForm({
      firstName: '',
      lastName: '',
      name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      emergencyContact: '',
      emergencyPhone: '',
      yearsExperience: '',
      previousExperience: '',
      certifications: [],
      ageGroupPreferences: [],
      subjectAreas: [],
      safeguardingTraining: false,
      cprTraining: false,
      backgroundCheckStatus: 'not_started',
      status: 'active',
      availability: {
        fullTime: false,
        partTime: false,
        emergencyOnly: false,
        longTerm: false
      },
      additionalInfo: ''
    });
    setShowModal(true);
    setError('');
  };

  const openEditModal = (substitute) => {
    setEditingSubstitute(substitute);
    setSubstituteForm({
      firstName: substitute.firstName || '',
      lastName: substitute.lastName || '',
      name: substitute.name || '',
      email: substitute.email || '',
      phone: substitute.phone || '',
      address: substitute.address || '',
      city: substitute.city || '',
      state: substitute.state || '',
      zip: substitute.zip || '',
      emergencyContact: substitute.emergencyContact || '',
      emergencyPhone: substitute.emergencyPhone || '',
      yearsExperience: substitute.yearsExperience || '',
      previousExperience: substitute.previousExperience || '',
      certifications: substitute.certifications || [],
      ageGroupPreferences: substitute.ageGroupPreferences || [],
      subjectAreas: substitute.subjectAreas || [],
      safeguardingTraining: substitute.safeguardingTraining || false,
      cprTraining: substitute.cprTraining || false,
      backgroundCheckStatus: substitute.backgroundCheckStatus || 'not_started',
      status: substitute.status || 'active',
      availability: substitute.availability || {
        fullTime: false,
        partTime: false,
        emergencyOnly: false,
        longTerm: false
      },
      additionalInfo: substitute.additionalInfo || ''
    });
    setShowModal(true);
    setError('');
  };

  const handleSaveSubstitute = async () => {
    try {
      setSaving(true);
      setError('');

      // Basic validation
      if (!substituteForm.firstName || !substituteForm.lastName || !substituteForm.email) {
        setError('Please fill in first name, last name, and email.');
        return;
      }

      // Check if email already exists (but allow current substitute's email when editing)
      const existingSubQuery = query(
        collection(db, 'faculty_staff'),
        where('tenantId', '==', currentUser.tenantId),
        where('email', '==', substituteForm.email.toLowerCase())
      );
      const existingSnapshot = await getDocs(existingSubQuery);
      
      if (!existingSnapshot.empty) {
        const existingDoc = existingSnapshot.docs[0];
        // Allow if this is the same substitute we're editing
        if (!editingSubstitute || existingDoc.id !== editingSubstitute.id) {
          setError('A substitute with this email address already exists.');
          return;
        }
      }

      const substituteData = {
        ...substituteForm,
        name: `${substituteForm.firstName} ${substituteForm.lastName}`,
        email: substituteForm.email.toLowerCase(),
        role: 'Outside Sub',
        department: 'Substitute',
        tenantId: currentUser.tenantId,
        substituteType: 'outside'
      };

      if (editingSubstitute) {
        // Update existing substitute
        substituteData.updatedAt = serverTimestamp();
        substituteData.updatedBy = currentUser.uid;

        await updateDoc(doc(db, 'faculty_staff', editingSubstitute.id), substituteData);
        console.log('Substitute updated:', editingSubstitute.id);
        
      } else {
        // Create new substitute
        substituteData.joinedAt = serverTimestamp();
        substituteData.createdAt = serverTimestamp();
        substituteData.createdBy = currentUser.uid;
        substituteData.registrationMethod = 'manual';

        const docRef = await addDoc(collection(db, 'faculty_staff'), substituteData);
        console.log('New substitute added with ID:', docRef.id);
      }
      
      // Close modal and reload data
      setShowModal(false);
      setEditingSubstitute(null);
      await loadSubstitutes();
      
    } catch (err) {
      console.error('Error saving substitute:', err);
      setError('Failed to save substitute. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const updateSubstituteField = (field, value) => {
    setSubstituteForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const toggleCertification = (cert) => {
    setSubstituteForm(prev => ({
      ...prev,
      certifications: prev.certifications.includes(cert)
        ? prev.certifications.filter(c => c !== cert)
        : [...prev.certifications, cert]
    }));
  };

  const toggleAgeGroup = (ageGroup) => {
    setSubstituteForm(prev => ({
      ...prev,
      ageGroupPreferences: prev.ageGroupPreferences.includes(ageGroup)
        ? prev.ageGroupPreferences.filter(a => a !== ageGroup)
        : [...prev.ageGroupPreferences, ageGroup]
    }));
  };

  const toggleSubjectArea = (subject) => {
    setSubstituteForm(prev => ({
      ...prev,
      subjectAreas: prev.subjectAreas.includes(subject)
        ? prev.subjectAreas.filter(s => s !== subject)
        : [...prev.subjectAreas, subject]
    }));
  };

  const toggleAvailability = (type) => {
    setSubstituteForm(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [type]: !prev.availability[type]
      }
    }));
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'No date';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getAvailabilityText = (availability) => {
  if (!availability) return 'Not specified';
  
  // Handle new registration form format (weekly schedule)
  if (availability.monday || availability.tuesday || availability.wednesday || 
      availability.thursday || availability.friday || availability.saturday || availability.sunday) {
    
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const availableTimes = [];
    
    days.forEach(day => {
      if (availability[day]) {
        const times = [];
        if (availability[day].morning) times.push('Morning');
        if (availability[day].afternoon) times.push('Afternoon'); 
        if (availability[day].evening) times.push('Evening');
        
        if (times.length > 0) {
          availableTimes.push(`${day.charAt(0).toUpperCase() + day.slice(1)}: ${times.join(', ')}`);
        }
      }
    });
    
    return availableTimes.length > 0 ? availableTimes.join('; ') : 'Not specified';
  }
  
  // Handle old format (availability types)
  const labels = {
    fullTime: 'Full Day',
    partTime: 'Partial Day',
    emergencyOnly: 'Emergency Only',
    longTerm: 'Long-term'
  };
  
  const availableTypes = Object.entries(availability)
    .filter(([type, available]) => available)
    .map(([type]) => labels[type]);
  
  return availableTypes.length > 0 ? availableTypes.join(', ') : 'No availability set';
};

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#28a745';
      case 'inactive': return '#6c757d';
      case 'pending': return '#ffc107';
      default: return '#6c757d';
    }
  };

  const filteredSubstitutes = substitutes.filter(substitute => {
    const matchesSearch = !searchTerm || 
      `${substitute.firstName} ${substitute.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      substitute.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesAgeGroup = !ageGroupFilter || 
      (substitute.ageGroupPreferences && substitute.ageGroupPreferences.includes(ageGroupFilter));
    
    const matchesSubject = !subjectFilter || 
      (substitute.subjectAreas && substitute.subjectAreas.includes(subjectFilter));
    
    return matchesSearch && matchesAgeGroup && matchesSubject;
  });

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '18px', color: '#666' }}>Loading substitute directory...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      {/* Header with Add Button */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '30px' }}>
        <div>
          <h1 style={{ margin: '0 0 10px 0', fontSize: '28px', fontWeight: 'bold', color: '#333' }}>
            Substitute Teacher Directory
          </h1>
          <p style={{ margin: 0, color: '#666', fontSize: '16px' }}>
            Browse and manage your substitute teacher database
          </p>
        </div>
        <button
          onClick={openAddModal}
          style={{
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            padding: '12px 20px',
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <span style={{ fontSize: '18px' }}>🎓</span>
          Add New Substitute
        </button>
      </div>

      {error && (
        <div style={{ 
          backgroundColor: '#f8d7da', 
          color: '#721c24', 
          padding: '12px', 
          borderRadius: '8px', 
          marginBottom: '20px',
          border: '1px solid #f5c6cb'
        }}>
          {error}
        </div>
      )}

      {/* Filters */}
      <div style={{ 
        backgroundColor: 'white', 
        padding: '20px', 
        borderRadius: '8px', 
        border: '1px solid #dee2e6',
        marginBottom: '20px'
      }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '20px',
          alignItems: 'end'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
              Search Substitutes
            </label>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
              Filter by Age Group
            </label>
            <select
              value={ageGroupFilter}
              onChange={(e) => setAgeGroupFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px'
              }}
            >
              <option value="">All Age Groups</option>
              {ageGroupOptions.map(age => (
                <option key={age} value={age}>{age}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
              Filter by Subject
            </label>
            <select
              value={subjectFilter}
              onChange={(e) => setSubjectFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px'
              }}
            >
              <option value="">All Subjects</option>
              {subjectOptions.map(subject => (
                <option key={subject} value={subject}>{subject}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
              Filter by Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px'
              }}
            >
              <option value="active">Active ({substitutes.filter(s => s.status === 'active').length})</option>
              <option value="inactive">Inactive ({substitutes.filter(s => s.status === 'inactive').length})</option>
              <option value="pending">Pending ({substitutes.filter(s => s.status === 'pending').length})</option>
              <option value="all">All ({substitutes.length})</option>
            </select>
          </div>

          <div>
            <button
              onClick={() => {
                setSearchTerm('');
                setAgeGroupFilter('');
                setSubjectFilter('');
                setStatusFilter('active');
              }}
              style={{
                width: '100%',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '12px',
                borderRadius: '4px',
                fontSize: '16px',
                cursor: 'pointer'
              }}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Results Summary */}
      <div style={{ marginBottom: '20px', color: '#666' }}>
        Showing {filteredSubstitutes.length} of {substitutes.length} substitute teachers
      </div>

      {/* Substitutes Grid */}
      {filteredSubstitutes.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          backgroundColor: 'white', 
          borderRadius: '8px',
          border: '1px solid #dee2e6'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>🎓</div>
          <div style={{ fontSize: '18px', color: '#666', marginBottom: '10px' }}>
            No substitute teachers found
          </div>
          <div style={{ fontSize: '14px', color: '#999' }}>
            {searchTerm || ageGroupFilter || subjectFilter
              ? 'Try adjusting your search filters.'
              : 'No substitute teachers have been approved yet.'
            }
          </div>
        </div>
      ) : (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', 
          gap: '20px' 
        }}>
          {filteredSubstitutes.map(substitute => (
            <div 
              key={substitute.id} 
              style={{ 
                backgroundColor: 'white', 
                borderRadius: '8px', 
                border: '1px solid #dee2e6',
                padding: '20px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onClick={() => setSelectedSubstitute(selectedSubstitute === substitute.id ? null : substitute.id)}
            >
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div>
                  <h3 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: 'bold', color: '#333' }}>
                    {substitute.firstName} {substitute.lastName}
                    {substitute.registrationMethod === 'manual' && (
                      <span style={{ 
                        marginLeft: '8px', 
                        fontSize: '12px', 
                        color: '#6c757d',
                        fontWeight: 'normal'
                      }}>
                        (Added manually)
                      </span>
                    )}
                  </h3>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    {substitute.email}
                  </div>
                </div>
                <span style={{
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  backgroundColor: getStatusColor(substitute.status),
                  color: 'white',
                  textTransform: 'capitalize'
                }}>
                  {substitute.status}
                </span>
              </div>

              {/* Basic Info */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
                  📞 {substitute.phone || 'No phone provided'}
                </div>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
                  📅 Joined {formatDate(substitute.joinedAt)}
                </div>
                <div style={{ fontSize: '14px', color: '#666' }}>
                  📚 {substitute.yearsExperience || 'Experience not specified'}
                </div>
              </div>

              {/* Age Groups */}
              {substitute.ageGroupPreferences && substitute.ageGroupPreferences.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#555', marginBottom: '6px' }}>
                    AGE GROUPS
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {substitute.ageGroupPreferences.slice(0, 2).map(age => (
                      <span 
                        key={age}
                        style={{
                          padding: '2px 6px',
                          backgroundColor: '#e9ecef',
                          borderRadius: '12px',
                          fontSize: '11px',
                          color: '#495057'
                        }}
                      >
                        {age}
                      </span>
                    ))}
                    {substitute.ageGroupPreferences.length > 2 && (
                      <span style={{
                        padding: '2px 6px',
                        backgroundColor: '#6c757d',
                        borderRadius: '12px',
                        fontSize: '11px',
                        color: 'white'
                      }}>
                        +{substitute.ageGroupPreferences.length - 2} more
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Expanded Details */}
              {selectedSubstitute === substitute.id && (
                <div style={{ 
                  borderTop: '1px solid #dee2e6', 
                  paddingTop: '16px', 
                  marginTop: '16px',
                  fontSize: '14px'
                }}>
                  {/* Full Address */}
                  {substitute.address && (
                    <div style={{ marginBottom: '12px' }}>
                      <strong>Address:</strong><br />
                      {substitute.address}<br />
                      {substitute.city}, {substitute.state} {substitute.zip}
                    </div>
                  )}

                  {/* Emergency Contact */}
                  {substitute.emergencyContact && (
                    <div style={{ marginBottom: '12px' }}>
                      <strong>Emergency Contact:</strong><br />
                      {substitute.emergencyContact} - {substitute.emergencyPhone}
                    </div>
                  )}

                  {/* Experience */}
                  {substitute.previousExperience && (
                    <div style={{ marginBottom: '12px' }}>
                      <strong>Previous Experience:</strong><br />
                      <div style={{ 
                        padding: '8px', 
                        backgroundColor: '#f8f9fa', 
                        borderRadius: '4px',
                        fontSize: '13px',
                        color: '#555',
                        marginTop: '4px'
                      }}>
                        {substitute.previousExperience}
                      </div>
                    </div>
                  )}

                  {/* All Age Groups */}
                  {substitute.ageGroupPreferences && substitute.ageGroupPreferences.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <strong>All Age Groups:</strong><br />
                      {substitute.ageGroupPreferences.join(', ')}
                    </div>
                  )}

                  {/* Subject Areas */}
                  {substitute.subjectAreas && substitute.subjectAreas.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <strong>Subject Areas:</strong><br />
                      {substitute.subjectAreas.join(', ')}
                    </div>
                  )}

                  {/* Certifications */}
                  {substitute.certifications && substitute.certifications.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <strong>Certifications:</strong><br />
                      {Array.isArray(substitute.certifications) 
                    ? substitute.certifications.join(', ') 
                    : substitute.certifications || 'None listed'}
                    </div>
                  )}

                  {/* Training */}
                  <div style={{ marginBottom: '12px' }}>
                    <strong>Training:</strong><br />
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      Safeguarding: {substitute.safeguardingTraining ? 'Completed' : 'Not completed'}<br />
                      CPR/First Aid: {substitute.cprTraining ? 'Completed' : 'Not completed'}
                    </div>
                  </div>

                  {/* Availability */}
                  <div style={{ marginBottom: '12px' }}>
                    <strong>Availability:</strong><br />
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      {getAvailabilityText(substitute.availability)}
                    </div>
                  </div>

                  {/* Background Check Status */}
                  <div style={{ marginBottom: '12px' }}>
                    <strong>Background Check:</strong>{' '}
                    <span style={{
                      padding: '2px 6px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      backgroundColor: substitute.backgroundCheckStatus === 'cleared' ? '#d4edda' : 
                                     substitute.backgroundCheckStatus === 'pending' ? '#fff3cd' : '#f8d7da',
                      color: substitute.backgroundCheckStatus === 'cleared' ? '#155724' : 
                             substitute.backgroundCheckStatus === 'pending' ? '#856404' : '#721c24'
                    }}>
                      {substitute.backgroundCheckStatus || 'Not Started'}
                    </span>
                  </div>

                  {/* Additional Info */}
                  {substitute.additionalInfo && (
                    <div style={{ marginBottom: '12px' }}>
                      <strong>Additional Info:</strong><br />
                      <div style={{ 
                        padding: '8px', 
                        backgroundColor: '#f8f9fa', 
                        borderRadius: '4px',
                        fontSize: '13px',
                        color: '#555',
                        marginTop: '4px'
                      }}>
                        {substitute.additionalInfo}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div style={{ display: 'flex', gap: '8px', marginTop: '16px', flexWrap: 'wrap' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditModal(substitute);
                      }}
                      style={{
                        backgroundColor: '#ffc107',
                        color: '#212529',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      Edit
                    </button>
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        window.location.href = `mailto:${substitute.email}`;
                      }}
                      style={{
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      Email
                    </button>
                    
                    {substitute.phone && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          window.location.href = `tel:${substitute.phone}`;
                        }}
                        style={{
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Call
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Click to expand hint */}
              <div style={{ 
                textAlign: 'center', 
                fontSize: '12px', 
                color: '#999', 
                marginTop: '12px',
                borderTop: selectedSubstitute === substitute.id ? 'none' : '1px solid #f0f0f0',
                paddingTop: selectedSubstitute === substitute.id ? '0' : '8px'
              }}>
                {selectedSubstitute === substitute.id ? 'Click to collapse' : 'Click to view details'}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary Stats */}
      {substitutes.length > 0 && (
        <div style={{ 
          marginTop: '30px',
          padding: '20px',
          backgroundColor: 'white',
          borderRadius: '8px',
          border: '1px solid #dee2e6'
        }}>
          <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', fontWeight: 'bold', color: '#333' }}>
            Substitute Teacher Summary
          </h3>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '20px' 
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>
                {substitutes.filter(s => s.status === 'active').length}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>Active Substitutes</div>
            </div>
            
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#007bff' }}>
                {[...new Set(substitutes.flatMap(s => s.ageGroupPreferences || []))].length}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>Age Groups Covered</div>
            </div>
            
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#6f42c1' }}>
                {[...new Set(substitutes.flatMap(s => s.subjectAreas || []))].length}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>Subject Areas</div>
            </div>
            
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#fd7e14' }}>
                {substitutes.filter(s => s.backgroundCheckStatus === 'cleared').length}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>Background Checks Cleared</div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Substitute Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            width: '100%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
              <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color: '#333' }}>
                {editingSubstitute ? 'Edit Substitute Teacher' : 'Add New Substitute Teacher'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#999'
                }}
              >
                ×
              </button>
            </div>

            {/* Basic Information */}
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', fontWeight: 'bold', color: '#495057' }}>
                Basic Information
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={substituteForm.firstName}
                    onChange={(e) => updateSubstituteField('firstName', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={substituteForm.lastName}
                    onChange={(e) => updateSubstituteField('lastName', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                  />
                </div>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    value={substituteForm.email}
                    onChange={(e) => updateSubstituteField('email', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={substituteForm.phone}
                    onChange={(e) => updateSubstituteField('phone', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Experience */}
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', fontWeight: 'bold', color: '#495057' }}>
                Experience & Training
              </h3>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                  Years of Experience
                </label>
                <select
                  value={substituteForm.yearsExperience}
                  onChange={(e) => updateSubstituteField('yearsExperience', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '16px'
                  }}
                >
                  <option value="">Select experience level...</option>
                  <option value="0">No formal experience</option>
                  <option value="1-2">1-2 years</option>
                  <option value="3-5">3-5 years</option>
                  <option value="6-10">6-10 years</option>
                  <option value="11-15">11-15 years</option>
                  <option value="16+">16+ years</option>
                </select>
              </div>
              
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                  Previous Experience
                </label>
                <textarea
                  value={substituteForm.previousExperience}
                  onChange={(e) => updateSubstituteField('previousExperience', e.target.value)}
                  placeholder="Describe previous teaching or educational experience..."
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '16px',
                    minHeight: '80px',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', padding: '15px', border: '2px solid #ddd', borderRadius: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={substituteForm.safeguardingTraining}
                    onChange={(e) => updateSubstituteField('safeguardingTraining', e.target.checked)}
                    style={{ marginRight: '12px', transform: 'scale(1.2)' }}
                  />
                  <div>
                    <div style={{ fontWeight: 'bold' }}>Safeguarding Training</div>
                    <div style={{ fontSize: '14px', color: '#666' }}>Completed safeguarding training</div>
                  </div>
                </label>
                
                <label style={{ display: 'flex', alignItems: 'center', padding: '15px', border: '2px solid #ddd', borderRadius: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={substituteForm.cprTraining}
                    onChange={(e) => updateSubstituteField('cprTraining', e.target.checked)}
                    style={{ marginRight: '12px', transform: 'scale(1.2)' }}
                  />
                  <div>
                    <div style={{ fontWeight: 'bold' }}>CPR/First Aid Training</div>
                    <div style={{ fontSize: '14px', color: '#666' }}>Current certification</div>
                  </div>
                </label>
              </div>
            </div>

            {/* Certifications */}
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', fontWeight: 'bold', color: '#495057' }}>
                Certifications
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                {certificationOptions.map(cert => (
                  <label key={cert} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={substituteForm.certifications.includes(cert)}
                      onChange={() => toggleCertification(cert)}
                    />
                    <span style={{ fontSize: '14px' }}>{cert}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Age Groups */}
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', fontWeight: 'bold', color: '#495057' }}>
                Preferred Age Groups
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                {ageGroupOptions.map(age => (
                  <label key={age} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={substituteForm.ageGroupPreferences.includes(age)}
                      onChange={() => toggleAgeGroup(age)}
                    />
                    <span style={{ fontSize: '14px' }}>{age}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Subject Areas */}
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', fontWeight: 'bold', color: '#495057' }}>
                Subject Areas
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                {subjectOptions.map(subject => (
                  <label key={subject} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={substituteForm.subjectAreas.includes(subject)}
                      onChange={() => toggleSubjectArea(subject)}
                    />
                    <span style={{ fontSize: '14px' }}>{subject}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Availability */}
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', fontWeight: 'bold', color: '#495057' }}>
                Availability Type
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                {Object.entries({
                  fullTime: 'Full Day',
                  partTime: 'Partial Day', 
                  emergencyOnly: 'Emergency Only',
                  longTerm: 'Long-term'
                }).map(([type, label]) => (
                  <label key={type} style={{ display: 'flex', alignItems: 'center', padding: '15px', border: '2px solid #ddd', borderRadius: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={substituteForm.availability[type]}
                      onChange={() => toggleAvailability(type)}
                      style={{ marginRight: '12px', transform: 'scale(1.2)' }}
                    />
                    <div style={{ fontWeight: 'bold' }}>{label}</div>
                  </label>
                ))}
              </div>
            </div>

            {/* Status and Background Check */}
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', fontWeight: 'bold', color: '#495057' }}>
                Status & Background Check
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                    Status
                  </label>
                  <select
                    value={substituteForm.status}
                    onChange={(e) => updateSubstituteField('status', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                    Background Check Status
                  </label>
                  <select
                    value={substituteForm.backgroundCheckStatus}
                    onChange={(e) => updateSubstituteField('backgroundCheckStatus', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                  >
                    <option value="not_started">Not Started</option>
                    <option value="pending">Pending</option>
                    <option value="cleared">Cleared</option>
                    <option value="denied">Denied</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', fontWeight: 'bold', color: '#495057' }}>
                Additional Information
              </h3>
              <textarea
                value={substituteForm.additionalInfo}
                onChange={(e) => updateSubstituteField('additionalInfo', e.target.value)}
                placeholder="Any additional notes about this substitute teacher..."
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '16px',
                  minHeight: '100px',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Modal Actions */}
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowModal(false)}
                disabled={saving}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '4px',
                  fontSize: '16px',
                  cursor: saving ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSubstitute}
                disabled={saving || !substituteForm.firstName || !substituteForm.lastName || !substituteForm.email}
                style={{
                  backgroundColor: saving ? '#ccc' : '#28a745',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '4px',
                  fontSize: '16px',
                  cursor: (saving || !substituteForm.firstName || !substituteForm.lastName || !substituteForm.email) ? 'not-allowed' : 'pointer'
                }}
              >
                {saving ? (editingSubstitute ? 'Updating...' : 'Adding...') : (editingSubstitute ? 'Update Substitute' : 'Add Substitute')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubstituteDirectory;